/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { computed } from '@ember/object';
import { isEmpty, isNone } from '@ember/utils';
import DeviceAttributeCondition, {
  getSpecialDeviceAttributeConfig,
  SpecialDeviceAttributes,
} from 'cio/app-models/conditions/device-attribute';
import { DataSourceLabelVariant } from 'cio/utils/data-source-label';
import CioConditionViewerBase, { CioConditionViewerBaseArgs } from 'cio/components/cio-condition-viewer-base';
import DevicesService from 'cio/services/devices';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TaskInstance } from 'ember-concurrency';

type OperatorLabel = Record<string, string>;

const OPERATORS: Record<string, OperatorLabel> = {
  id: {
    exists: 'have',
    '!exists': "don't have",
  },
  platform: {
    eq: 'equal to',
    '!eq': 'not equal to',
  },
  _last_status: {
    eq: 'is',
    '!eq': 'is not',
  },
  device_model: {
    eq: 'equal to',
    '!eq': 'not equal to',
  },
};

interface CioConditionViewerDeviceAttributeArgs extends CioConditionViewerBaseArgs {
  condition: DeviceAttributeCondition;
  showName: boolean;
  depth?: number;
  labelVariant?: DataSourceLabelVariant;
  labelTruncate?: boolean;
  labelClasses?: string;
}

export default class CioConditionViewerDeviceAttribute extends CioConditionViewerBase<CioConditionViewerDeviceAttributeArgs> {
  @service devices!: DevicesService;

  @tracked devicesTaskInstance: TaskInstance<unknown> | null = null;

  constructor(owner: unknown, args: CioConditionViewerDeviceAttributeArgs) {
    super(owner, args);

    this.devicesTaskInstance = this.devices.load.perform();
  }

  get attribute() {
    return this.isDeviceExistsCondition ? '' : this.attributeLabel;
  }

  @computed('args.condition.name')
  get attributeLabel() {
    let { condition } = this.args;
    return getSpecialDeviceAttributeConfig(condition.name)?.label ?? condition.name;
  }

  @computed('args.condition.{name,operator,value}')
  get conditionModel() {
    let condition = { ...this.args.condition };
    if (this.isSpecialDeviceAttribute) {
      condition.name = this.attributeLabel;
    }
    return condition;
  }

  get hasCustomDisplay() {
    let hasCustomDisplay = this.isSpecialDeviceAttribute;

    if (this.isDeviceModelCondition) {
      // for other operators show the default viewer
      let { operator } = this.args.condition;
      hasCustomDisplay = Object.keys(OPERATORS.device_model).includes(operator);
    }
    return hasCustomDisplay;
  }

  @computed('args.condition.name')
  get isDeviceExistsCondition() {
    let { name } = this.args.condition;
    return name === 'id';
  }

  @computed('args.condition.operator')
  get isDeviceNeverUsedCondition() {
    let { condition } = this.args;
    return this.isLastUsedCondition && condition.operator === '!exists';
  }

  @computed('args.condition.name')
  get isLastUsedCondition() {
    return this.args.condition.name === SpecialDeviceAttributes._last_used;
  }

  @computed('args.condition.name')
  get isLastStatusCondition() {
    return this.args.condition.name === SpecialDeviceAttributes._last_status;
  }

  @computed('args.condition.name')
  get isDeviceModelCondition() {
    return this.args.condition.name === SpecialDeviceAttributes.device_model;
  }

  @computed('args.condition.isSpecialAttribute')
  get isSpecialDeviceAttribute() {
    let { isSpecialAttribute } = this.args.condition;
    return isSpecialAttribute;
  }

  @computed('args.condition.operator')
  get isTimestampLastUsedCondition() {
    let { condition } = this.args;
    return this.isLastUsedCondition && condition.operator !== '!exists';
  }

  @computed('args.condition.{name,operator}')
  get operator() {
    let { condition } = this.args;
    if (this.isDeviceExistsCondition) {
      let notExists = condition.operator.charAt(0) === '!';
      return notExists ? "don't have" : 'have';
    } else if (this.isLastUsedCondition) {
      return this.isDeviceNeverUsedCondition ? 'have device that' : '';
    } else if (this.isLastStatusCondition && condition.operator.endsWith('exists')) {
      // exists = is not unused, !exists = is unused
      return condition.operator.charAt(0) === '!' ? 'is' : 'is not';
    }

    return this.operatorDetails;
  }

  @computed('args.condition.{name,operator}')
  get operatorDetails() {
    let { name, operator } = this.args.condition;
    return OPERATORS[name][operator];
  }

  @computed('args.showName', 'args.condition.{name,operator}')
  get showName() {
    let shouldAlwaysOmitAttributeName = this.isDeviceExistsCondition || this.isDeviceNeverUsedCondition;
    if (shouldAlwaysOmitAttributeName) {
      return false;
    }
    let { showName } = this.args;
    return isNone(showName) ? true : showName;
  }

  @computed('args.condition.{name,operator,value}', 'devicesTaskInstance.isRunning')
  get value() {
    let { operator, name, value } = this.args.condition;

    if (this.isDeviceExistsCondition) {
      let notExists = operator.charAt(0) === '!';
      return notExists ? 'any devices' : 'a device';
    } else if (this.isLastUsedCondition) {
      return this.isDeviceNeverUsedCondition ? 'has not been used' : '';
    } else if (this.isLastStatusCondition && operator.endsWith('exists')) {
      return 'unused';
    } else if (this.isDeviceModelCondition) {
      let marketingName = this.devices.getMarketingName(value);
      return marketingName ?? value;
    } else {
      let conditionValues = getSpecialDeviceAttributeConfig(name)?.values ?? [];
      if (!isEmpty(conditionValues)) {
        return conditionValues.findBy('value', value)?.label;
      }
    }
    return '';
  }
}
